import React from 'react';
import { Navigate } from 'react-router-dom';
import { PrivateRoute } from 'shared/components/PrivateRoute';
import { paths } from 'shared/routing/paths';
import { OperationsLayout } from 'features/operations/components/OperationsLayout';
import { defineRoute, AppRoute } from './types';
import { lazyNamed } from './lazy';

const OperationsScreeningReviewsPage = lazyNamed(
  () => import('features/operations/pages/OperationsScreeningReviewsPage'),
  'OperationsScreeningReviewsPage',
);
const OperationsCampaignsPage = lazyNamed(
  () => import('features/operations/pages/OperationsCampaignsPage'),
  'OperationsCampaignsPage',
);
const OperationsExpertPoolsPage = lazyNamed(
  () => import('features/operations/pages/OperationsExpertPoolsPage'),
  'OperationsExpertPoolsPage',
);
const OperationsApplicationsPage = lazyNamed(
  () => import('features/operations/pages/OperationsApplicationsPage'),
  'OperationsApplicationsPage',
);

const OperationsPoolSignupsPage = lazyNamed(
  () => import('features/operations/pages/OperationsPoolSignupsPage'),
  'OperationsPoolSignupsPage',
);

const OperationsOutreachHomePage = lazyNamed(
  () => import('features/operations/pages/OperationsOutreachHomePage'),
  'OperationsOutreachHomePage',
);

const OperationsOutreachCandidatesPage = lazyNamed(
  () => import('features/operations/pages/OperationsOutreachCandidatesPage'),
  'OperationsOutreachCandidatesPage',
);

const OperationsOutreachDashboardPage = lazyNamed(
  () => import('features/operations/pages/OperationsOutreachDashboardPage'),
  'OperationsOutreachDashboardPage',
);

const OperationsOutreachSignupsPage = lazyNamed(
  () => import('features/operations/pages/OperationsOutreachSignupsPage'),
  'OperationsOutreachSignupsPage',
);

const OperationsOutreachSearchesPage = lazyNamed(
  () => import('features/operations/pages/OperationsOutreachSearchesPage'),
  'OperationsOutreachSearchesPage',
);

const OperationsOutreachSettingsPage = lazyNamed(
  () => import('features/operations/pages/OperationsOutreachSettingsPage'),
  'OperationsOutreachSettingsPage',
);

const operationsRootId = 'operations.shell';

function operationsChild(id: string, path: string, element: React.ReactNode): AppRoute {
  return defineRoute(
    {
      id,
      path,
      area: 'operations',
      guard: 'inherited',
      layout: 'full-width',
      kind: 'page',
      parentId: operationsRootId,
    },
    { path: path.slice(paths.operations.root.length + 1), element },
  );
}

const operationsRootRoute = defineRoute(
  {
    id: operationsRootId,
    path: paths.operations.root,
    area: 'operations',
    guard: 'operations',
    layout: 'full-width',
    kind: 'layout',
  },
  {
    path: paths.operations.root,
    element: (
      <PrivateRoute requireRole="operations">
        <OperationsLayout />
      </PrivateRoute>
    ),
    children: [
      defineRoute(
        {
          id: 'operations.index',
          path: paths.operations.root,
          area: 'operations',
          guard: 'inherited',
          layout: 'full-width',
          kind: 'redirect',
          parentId: operationsRootId,
          redirectTo: paths.operations.campaigns,
        },
        { index: true, element: <Navigate to={paths.operations.campaigns} replace /> },
      ),
      operationsChild('operations.campaigns', paths.operations.campaigns, <OperationsCampaignsPage />),
      operationsChild('operations.expertPools', paths.operations.expertPools, <OperationsExpertPoolsPage />),
      operationsChild('operations.applications', paths.operations.applications, <OperationsApplicationsPage />),
      operationsChild('operations.newExpertTracker', paths.operations.newExpertTracker, <OperationsPoolSignupsPage />),
      operationsChild('operations.screeningReviews', paths.operations.screeningReviews, <OperationsScreeningReviewsPage />),
      operationsChild('operations.outreach', paths.operations.outreach, <OperationsOutreachHomePage />),
      operationsChild('operations.outreachCandidates', paths.operations.outreachCandidates, <OperationsOutreachCandidatesPage />),
      operationsChild('operations.outreachDashboard', paths.operations.outreachDashboard, <OperationsOutreachDashboardPage />),
      operationsChild('operations.outreachSignups', paths.operations.outreachSignups, <OperationsOutreachSignupsPage />),
      operationsChild('operations.outreachSearches', paths.operations.outreachSearches, <OperationsOutreachSearchesPage />),
      operationsChild('operations.outreachSettings', paths.operations.outreachSettings, <OperationsOutreachSettingsPage />),
    ],
  },
);

export const OPERATIONS_ROUTES: AppRoute[] = [operationsRootRoute];
